import axios from 'axios'
import { getIdToken, resolveCurrentUid } from '@/utils/auth'
import { API_BASE } from '@/services/api'
import { clearCachedSpreadsheets } from '@/hooks/spreadsheetsCache'
import { beginGooglePkce } from '@/utils/googlePkce'
import {
  SheetCreationStatusSchema,
  type SheetCreationStatus,
} from '@shared/schemas/sheetOperations.schema'
import { TemplatePreviewResponseSchema } from '@shared/schemas/sheetsApi.schema'

/**
 * Build the Google consent URL for a bound start. `nonce` is the one the caller's
 * session store already put in `redirectPath`; a PKCE verifier is stored against
 * it here, so every start site gets both or cannot leave for Google at all.
 */
export const getGoogleOAuthUrl = async (
  enableRefresh: boolean,
  redirectPath: string,
  nonce: string | null
) => {
  if (!nonce) throw new Error('Google authorization must start from a saved session.')
  const codeChallenge = await beginGooglePkce(nonce)
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const redirectUri = import.meta.env.DEV
    ? 'http://localhost:5173/oauth/callback'
    : import.meta.env.VITE_REDIRECT_URI
  const scope = [
    'https://www.googleapis.com/auth/drive.file',
    'https://www.googleapis.com/auth/drive.readonly',
  ].join(' ')

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope,
    state: redirectPath,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    ...(enableRefresh && { access_type: 'offline', prompt: 'consent' }),
  })

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
}

/** After redirect, exchange Google auth code for access + refresh tokens */
export const oauthCallback = async (
  code: string,
  codeVerifier: string,
  expectedUid?: string
): Promise<void> => {
  if (expectedUid && (await resolveCurrentUid()) !== expectedUid)
    throw new Error('Signed-in user changed')
  const token = await getIdToken()
  if (expectedUid && (await resolveCurrentUid()) !== expectedUid)
    throw new Error('Signed-in user changed')
  await axios.get(`${API_BASE}/oauthCallback`, {
    params: { code, code_verifier: codeVerifier },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

/** Create or append to a spreadsheet */
export const createSpreadsheet = async ({
  sheetSelection,
  spreadsheetTitle,
  existingSpreadsheetId,
  existingSheetTab,
  exportType,
  folderId,
  transactions,
  categorizeTransactions,
  selectedAccountIds,
  selectedAccounts,
  enableAutomaticSyncing,
  syncFrequency,
  templateId,
  signal,
  operationId,
  expectedUid,
}: {
  operationId?: string
  expectedUid?: string
  sheetSelection: 'new' | 'existing' | 'template'
  spreadsheetTitle?: string
  existingSpreadsheetId?: string
  existingSheetTab?: string
  exportType: 'monthly' | 'accounts' | 'single'
  folderId?: string
  /** Optional deadline. Creating a sheet is the slowest call in either flow and
   *  axios has no default timeout, so without one a stall never resolves. */
  signal?: AbortSignal
  transactions: {
    date: string
    name: string
    amount: number
    account_mask?: string
    account_name?: string
    direction: string
    original_description?: string
    primary_category?: string
    category_confidence_level?: string
    detailed_category?: string
    pending: boolean
    merchant_name?: string
    payment_channel?: string
    ai_category?: string
    ai_confidence?: number
  }[]
  /** Run categorization after the backend applies the selected account filter. */
  categorizeTransactions?: boolean
  selectedAccountIds?: string[]
  /**
   * The same filter, qualified by the bank each account belongs to.
   *
   * A Plaid account id is unique within its Item, not across them, so the bare
   * list cannot say which bank an account is. The backend prefers this when it
   * is present and falls back to the bare list, which V0 still sends.
   */
  selectedAccounts?: { itemId: string; accountId: string }[]
  enableAutomaticSyncing?: boolean
  syncFrequency?: string
  templateId?: string
}): Promise<{ spreadsheetId: string; spreadsheetUrl?: string; warnings?: string[] }> => {
  if (expectedUid && (await resolveCurrentUid()) !== expectedUid)
    throw new Error('Signed-in user changed')
  const token = await getIdToken()
  if (expectedUid && (await resolveCurrentUid()) !== expectedUid)
    throw new Error('Signed-in user changed')
  const { data } = await axios.post(
    `${API_BASE}/sheetsApi/v1/createSpreadsheet`,
    {
      sheetSelection,
      spreadsheetTitle,
      existingSpreadsheetId,
      existingSheetTab,
      exportType,
      folderId,
      transactions,
      categorizeTransactions,
      selectedAccountIds,
      selectedAccounts,
      enableAutomaticSyncing,
      syncFrequency,
      templateId,
      operationId,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }
  )

  // The dashboard caches the spreadsheet list to stop it flashing empty on every
  // view switch; a new sheet would not appear until that cache expired otherwise.
  // Clearing here rather than at each call site means a future caller cannot
  // forget it. Imported from the cache module directly, not via the hook that also
  // re-exports it, so this service does not pull React in.
  clearCachedSpreadsheets()

  return data
}

export type { SheetCreationStatus } from '@shared/schemas/sheetOperations.schema'

export async function getSheetCreationStatus(
  uid: string,
  operationId: string,
  signal: AbortSignal
): Promise<SheetCreationStatus> {
  if ((await resolveCurrentUid()) !== uid) throw new Error('Signed-in user changed')
  const token = await getIdToken()
  if ((await resolveCurrentUid()) !== uid) throw new Error('Signed-in user changed')
  const { data } = await axios.get(
    `${API_BASE}/sheetsApi/v1/creationStatus/${encodeURIComponent(operationId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }
  )
  const parsed = SheetCreationStatusSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error('Could not verify the previous spreadsheet creation')
  }
  if (parsed.data.status === 'succeeded') clearCachedSpreadsheets()
  return parsed.data
}

/**
 * The template's own Google Sheets URL, or null when this deployment has no
 * template configured or the lookup fails.
 *
 * Never throws: the only caller is a preview page that still works without the
 * link, so a failure here hides a button rather than surfacing an error.
 */
export async function fetchTemplatePreviewUrl(signal?: AbortSignal): Promise<string | null> {
  try {
    const token = await getIdToken()
    const { data } = await axios.get(`${API_BASE}/sheetsApi/v1/templatePreview`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    })
    const parsed = TemplatePreviewResponseSchema.safeParse(data)
    return parsed.success ? parsed.data.url : null
  } catch {
    return null
  }
}

/** Append transaction data to spreadsheet */
export const appendTransactionsToSheet = async ({
  spreadsheetId,
  transactions,
  exportType,
  lastSyncedTabs,
}: {
  spreadsheetId: string
  transactions: {
    date: string
    name: string
    amount: number
    account_mask?: string
    account_name?: string
    direction: string
    original_description?: string
    primary_category?: string
    detailed_category?: string
    pending: boolean
    merchant_name?: string
    payment_channel?: string
    ai_category?: string
    ai_confidence?: number
  }[]
  exportType?: 'monthly' | 'accounts' | 'single'
  lastSyncedTabs?: string[]
}) => {
  const token = await getIdToken()
  await axios.post(
    `${API_BASE}/sheetsApi/v1/appendTransactions`,
    { spreadsheetId, transactions, exportType, lastSyncedTabs },
    { headers: { Authorization: `Bearer ${token}` } }
  )
}

/** List user's spreadsheets */
export const listUserSpreadsheets = async (): Promise<
  { id: string; title: string; modifiedAt?: string }[]
> => {
  const token = await getIdToken()

  const { data } = await axios.get(`${API_BASE}/sheetsApi/v1/listSpreadsheets`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return data.spreadsheets || []
}
